import React, { useEffect, useId, useImperativeHandle, useRef, useState } from 'react';
import classNames from 'classnames';

import { useIntl } from '../../../util/reactIntl';
import { fetchSellerSuggestions } from '../../../util/api';
import {
  normalizeMainSearchQuery,
  shouldFetchMainSearchSuggestions,
  resolveMainSearchSubmit,
} from '../../../util/mainSearchQuery';

import { IconSearch } from '../../../components';

import {
  avatarInitial,
  avatarTintForId,
  sellerDisplayName,
  splitMatchedText,
} from '../SellersFilter/sellersFilterHelpers';

import css from './MainSearchSuggest.module.css';

const DEBOUNCE_MS = 280;

const SellerAvatar = ({ seller, label, className }) => {
  const tint = avatarTintForId(seller?.id || label);
  const initial = avatarInitial(label);
  const style = {
    backgroundColor: tint,
    ...(seller?.avatarUrl ? { backgroundImage: `url(${seller.avatarUrl})` } : {}),
  };
  return (
    <span className={className} style={style} aria-hidden="true">
      {seller?.avatarUrl ? null : initial}
    </span>
  );
};

const HighlightedName = ({ text, query }) => {
  const parts = splitMatchedText(text, query);
  return (
    <>
      {parts.map((part, index) =>
        part.hit ? (
          <b key={index} className={css.hit}>
            {part.text}
          </b>
        ) : (
          <span key={index}>{part.text}</span>
        )
      )}
    </>
  );
};

/**
 * Main-search combobox: listing keywords (row 0) plus seller suggestions.
 *
 * @param {Object} props
 * @param {string} props.id
 * @param {string} props.value
 * @param {Function} props.onChange
 * @param {Function} props.onSubmitKeywords
 * @param {Function} props.onSubmitSeller
 * @param {string} props.placeholder
 * @param {string} props.ariaLabel
 * @param {string} [props.inputClassName]
 * @param {string} [props.rootClassName]
 * @param {string} [props.dropdownClassName]
 * @param {{ current: HTMLInputElement | null }} [props.inputRef]
 * @param {React.Node} [props.leading]
 * @param {React.Node} [props.trailing]
 * @param {string} [props.name]
 * @param {string} [props.autoComplete]
 * @param {string} [props.inputId]
 * @param {{ current: { submit: Function } | null }} [props.submitRef]
 * @param {Function} [props.onSuggestionsOpenChange]
 */
const MainSearchSuggest = props => {
  const intl = useIntl();
  const {
    id,
    value,
    onChange,
    onSubmitKeywords,
    onSubmitSeller,
    placeholder,
    ariaLabel,
    inputClassName,
    rootClassName,
    dropdownClassName,
    inputRef,
    leading,
    trailing,
    name = 'keywords',
    autoComplete = 'off',
    inputId,
    submitRef,
    onSuggestionsOpenChange,
  } = props;

  const listboxId = useId();
  const liveRegionId = useId();
  const innerInputRef = useRef(null);
  const rootRef = useRef(null);
  const debounceRef = useRef(null);
  const requestIdRef = useRef(0);
  const fallbackSubmitRef = useRef(null);

  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(false);
  const [results, setResults] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [statusMessage, setStatusMessage] = useState('');

  const valueRef = useRef(value);
  const activeIndexRef = useRef(activeIndex);
  const resultsRef = useRef(results);
  const openRef = useRef(open);
  const onSubmitKeywordsRef = useRef(onSubmitKeywords);
  const onSubmitSellerRef = useRef(onSubmitSeller);
  valueRef.current = value;
  activeIndexRef.current = activeIndex;
  resultsRef.current = results;
  openRef.current = open;
  onSubmitKeywordsRef.current = onSubmitKeywords;
  onSubmitSellerRef.current = onSubmitSeller;

  const normalizedQuery = normalizeMainSearchQuery(value);
  const showDropdown = open && shouldFetchMainSearchSuggestions(normalizedQuery);

  useEffect(() => {
    onSuggestionsOpenChange?.(showDropdown);
  }, [showDropdown, onSuggestionsOpenChange]);
  const listingsLabel = intl.formatMessage(
    { id: 'MainSearchSuggest.listingsRow' },
    { query: normalizedQuery }
  );

  const setInputNode = node => {
    innerInputRef.current = node;
    if (inputRef) {
      inputRef.current = node;
    }
  };

  const cancelPendingSuggest = () => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    requestIdRef.current += 1;
    setPending(false);
    setResults([]);
  };

  const commit = (overrides = {}) => {
    const resolved = resolveMainSearchSubmit({
      query: valueRef.current,
      activeIndex: activeIndexRef.current,
      sellers: resultsRef.current,
      dropdownOpen: openRef.current,
      ...overrides,
    });
    cancelPendingSuggest();
    setOpen(false);
    if (resolved.type === 'seller') {
      onSubmitSellerRef.current(resolved.seller);
    } else {
      // Pass the raw typed value so mergeKeywordSearchParams strips one `@` once.
      onSubmitKeywordsRef.current(valueRef.current);
    }
  };

  useImperativeHandle(submitRef || fallbackSubmitRef, () => ({
    submit: () => commit(),
  }));

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onPointerDown = event => {
      if (!rootRef.current?.contains(event.target)) {
        cancelPendingSuggest();
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  useEffect(() => {
    if (!showDropdown) {
      return;
    }
    const node = document.getElementById(`${id}-option-${activeIndex}`);
    if (typeof node?.scrollIntoView === 'function') {
      node.scrollIntoView({ block: 'nearest' });
    }
  }, [showDropdown, activeIndex, id]);

  const runSuggest = normalized => {
    if (!shouldFetchMainSearchSuggestions(normalized)) {
      setResults([]);
      setPending(false);
      setOpen(false);
      setStatusMessage('');
      return;
    }

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setPending(true);
    setOpen(true);
    setActiveIndex(0);
    setStatusMessage(intl.formatMessage({ id: 'SellersFilter.searching' }));

    fetchSellerSuggestions(normalized)
      .then(payload => {
        if (requestId !== requestIdRef.current) {
          return;
        }
        const sellers = Array.isArray(payload?.sellers) ? payload.sellers : [];
        setResults(sellers);
        setPending(false);
        setActiveIndex(0);
        setStatusMessage(
          sellers.length > 0
            ? intl.formatMessage({ id: 'SellersFilter.foundCount' }, { count: sellers.length })
            : ''
        );
      })
      .catch(() => {
        if (requestId !== requestIdRef.current) {
          return;
        }
        setResults([]);
        setPending(false);
        setActiveIndex(0);
        if (openRef.current) {
          setOpen(true);
        }
        setStatusMessage('');
      });
  };

  const handleQueryChange = next => {
    onChange(next);
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    requestIdRef.current += 1;
    const normalized = normalizeMainSearchQuery(next);
    if (!shouldFetchMainSearchSuggestions(normalized)) {
      setPending(false);
      setResults([]);
      setOpen(false);
      setStatusMessage('');
      return;
    }
    setPending(true);
    setOpen(true);
    setActiveIndex(0);
    setResults([]);
    debounceRef.current = setTimeout(() => runSuggest(normalized), DEBOUNCE_MS);
  };

  const openSuggestions = () => {
    const normalized = normalizeMainSearchQuery(valueRef.current);
    if (!shouldFetchMainSearchSuggestions(normalized)) {
      return;
    }
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    requestIdRef.current += 1;
    setOpen(true);
    setActiveIndex(0);
    setPending(true);
    setResults([]);
    debounceRef.current = setTimeout(() => runSuggest(normalized), DEBOUNCE_MS);
  };

  const handleKeyDown = event => {
    const optionCount = 1 + results.length;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      event.stopPropagation();
      if (!showDropdown) {
        openSuggestions();
        return;
      }
      setActiveIndex(index => (index + 1) % optionCount);
      return;
    }
    if (event.key === 'ArrowUp' && showDropdown) {
      event.preventDefault();
      event.stopPropagation();
      setActiveIndex(index => (index <= 0 ? optionCount - 1 : index - 1));
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      commit();
      return;
    }
    if (event.key === 'Escape' && open) {
      event.preventDefault();
      event.stopPropagation();
      cancelPendingSuggest();
      setOpen(false);
    }
  };

  const highlightSeller = sellerIndex => {
    setActiveIndex(sellerIndex);
  };

  return (
    <div className={classNames(css.root, rootClassName)} ref={rootRef}>
      <div className={css.fieldRow}>
        {leading}
        <div className={css.inputWrap}>
          <input
            id={inputId || id}
            data-testid={inputId}
            ref={setInputNode}
            className={classNames(css.input, inputClassName)}
            name={name}
            value={value}
            onChange={event => handleQueryChange(event.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            autoComplete={autoComplete}
            role="combobox"
            aria-label={ariaLabel}
            aria-haspopup="listbox"
            aria-expanded={showDropdown}
            aria-controls={showDropdown ? listboxId : undefined}
            aria-autocomplete="list"
            aria-activedescendant={
              showDropdown && activeIndex >= 0 ? `${id}-option-${activeIndex}` : undefined
            }
            aria-describedby={liveRegionId}
          />
          {pending ? <span className={css.spinner} aria-hidden="true" /> : null}
        </div>
        {trailing}
      </div>
      {showDropdown ? (
        <div className={classNames(css.dropdown, dropdownClassName)} id={listboxId} role="listbox">
          <div
            id={`${id}-option-0`}
            role="option"
            tabIndex={-1}
            aria-label={listingsLabel}
            aria-selected={activeIndex === 0}
            className={classNames(css.option, css.listingsOption, {
              [css.optionActive]: activeIndex === 0,
            })}
            onMouseEnter={() => setActiveIndex(0)}
            onMouseDown={event => event.preventDefault()}
            onClick={() => commit({ activeIndex: 0, dropdownOpen: true })}
          >
            <IconSearch rootClassName={css.listingsIcon} />
            <span className={css.optionBody}>
              <span className={css.listingsText}>{listingsLabel}</span>
            </span>
          </div>
          {results.map((seller, index) => {
            const label = sellerDisplayName(seller);
            const optionIndex = index + 1;
            return (
              <div
                key={seller.username || seller.id || label}
                id={`${id}-option-${optionIndex}`}
                role="option"
                tabIndex={-1}
                aria-label={label}
                aria-selected={optionIndex === activeIndex}
                className={classNames(css.option, {
                  [css.optionActive]: optionIndex === activeIndex,
                })}
                onMouseEnter={() => highlightSeller(optionIndex)}
                onMouseDown={event => event.preventDefault()}
                onClick={() => commit({ activeIndex: optionIndex, dropdownOpen: true })}
              >
                <SellerAvatar seller={seller} label={label} className={css.avatar} />
                <span className={css.optionBody}>
                  <span className={css.username}>
                    <HighlightedName text={label} query={normalizedQuery} />
                  </span>
                  <span className={css.sub}>
                    {intl.formatMessage(
                      { id: 'SellersFilter.listingCount' },
                      { count: seller.listingCount || 0 }
                    )}
                  </span>
                </span>
              </div>
            );
          })}
        </div>
      ) : null}
      <div id={liveRegionId} className={css.visuallyHidden} role="status" aria-live="polite">
        {statusMessage}
      </div>
    </div>
  );
};

export default MainSearchSuggest;
